import React from "react"
import { letter } from "../lib/format"
import QuestionContent from "./QuestionContent"
import RichContent from "./RichContent"
import ReferenceText from "./ReferenceText"
import type { ReferenceTextPayload } from "../types"

// Corpo da questão (enunciado + alternativas) compartilhado entre a tela do
// simulado e o preview/revisão do admin — o preview é idêntico à prova por
// construção, não por cópia de markup.
//
// mode "exam":   alternativas clicáveis, destaque navy na selecionada.
// mode "review": alternativas estáticas, gabarito destacado em verde.

interface QuestionCardProps {
  statement: string
  alternatives: string[]
  mode: "exam" | "review"
  selected?: number | null
  onSelect?: (index: number) => void
  correctIndex?: number
  referenceText?: ReferenceTextPayload | null
}

export default function QuestionCard({ statement, alternatives, mode, selected, onSelect, correctIndex, referenceText }: QuestionCardProps) {
  return (
    <>
      {/* Vem ANTES do enunciado: é o que a questão manda ler primeiro. */}
      {referenceText && <ReferenceText reference={referenceText} />}

      <div className="mt-5 text-lg leading-relaxed text-navy-900 sm:text-xl">
        <RichContent html={statement} />
      </div>

      <div className="mt-6 flex flex-col gap-3" role="group" aria-label="Alternativas">
        {alternatives.map((alt, i) => {
          if (mode === "exam") {
            const isSelected = selected === i
            return (
              <button
                key={i}
                type="button"
                onClick={() => onSelect?.(i)}
                aria-pressed={isSelected}
                className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors ${
                  isSelected
                    ? "border-navy-800 bg-navy-50 ring-1 ring-navy-800"
                    : "border-slate-200 bg-white hover:border-navy-300 hover:bg-slate-50"
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base font-bold ${
                    isSelected ? "bg-navy-800 text-white" : "bg-slate-100 text-navy-500"
                  }`}
                  aria-hidden="true"
                >
                  {letter(i)}
                </span>
                <span className="text-base leading-relaxed text-navy-800 sm:text-lg">
                  <QuestionContent text={alt} />
                </span>
              </button>
            )
          }

          const isCorrect = i === correctIndex
          return (
            <div
              key={i}
              className={`flex items-start gap-4 rounded-xl border p-4 ${
                isCorrect ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base font-bold ${
                  isCorrect ? "bg-emerald-500 text-white" : "bg-slate-100 text-navy-500"
                }`}
                aria-hidden="true"
              >
                {letter(i)}
              </span>
              <span className={`pt-1 text-base leading-relaxed sm:text-lg ${isCorrect ? "text-navy-900" : "text-navy-700"}`}>
                <QuestionContent text={alt} />
                {isCorrect && (
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 align-middle text-xs font-semibold text-emerald-700">
                    Gabarito
                  </span>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </>
  )
}
